import type {
  ApiFile,
  FormChecklistItem,
  FormChecklistValue,
} from '@/features/tasks/task-dialogs/fields/FormChecklistBlock';

import { isMediaName, isVoiceName } from './attachments';

/** «2», «1,5», «1:30» → часы числом. */
export function parseHours(raw?: string) {
  const s = String(raw ?? '')
    .trim()
    .replace(',', '.');
  if (!s) {
    return 0;
  }
  if (s.indexOf(':') > 0) {
    const [h, m] = s.split(':');
    return (parseFloat(h) || 0) + (parseFloat(m) || 0) / 60;
  }
  return parseFloat(s) || 0;
}

/** Часы числом → «2,5» (как в макете, с запятой). */
export function formatHours(h: number) {
  return (Math.round(h * 10) / 10).toString().replace('.', ',');
}

/** Часы числом → «HH:MM» для поля «Время на выполнение». */
export function hoursToHHMM(h: number) {
  const total = Math.round(h * 60);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(
    total % 60
  ).padStart(2, '0')}`;
}

/** Сумма часов пункта вместе с его подпунктами. */
export function itemHours(item: FormChecklistItem, items: FormChecklistItem[]) {
  const subs = items.filter((x) => x.parentId === item.id);
  return (
    parseHours(item.hours) +
    subs.reduce((acc, s) => acc + parseHours(s.hours), 0)
  );
}

/** Суммарное время всех чек-листов формы. */
export function totalChecklistHours(value: FormChecklistValue) {
  const items = value?.items ?? [];
  return items.reduce((acc, it) => acc + parseHours(it.hours), 0);
}

/** Время одного чек-листа (таба). */
export function checklistHours(value: FormChecklistValue, checklistId: number) {
  return (value?.items ?? [])
    .filter((x) => x.checklistId === checklistId)
    .reduce((acc, it) => acc + parseHours(it.hours), 0);
}

const MIME_BY_EXT: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  heic: 'image/heic',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
  ogg: 'audio/ogg',
  m4a: 'audio/mp4',
  pdf: 'application/pdf',
};

/** data-URL для превью и проигрывания локального (ещё не отправленного) файла. */
export function apiFileUrl(file: ApiFile) {
  const ext = (file.name.split('.').pop() ?? '').toLowerCase();
  const mime = MIME_BY_EXT[ext] ?? 'application/octet-stream';
  return `data:${mime};base64,${file.content}`;
}

export type SplitFiles = {
  media: ApiFile[];
  voices: ApiFile[];
  docs: ApiFile[];
};

/** Раскладка вложений пункта по трём полосам макета. */
export function splitFiles(files?: ApiFile[]): SplitFiles {
  const all = files ?? [];
  return {
    media: all.filter((f) => isMediaName(f.name)),
    voices: all.filter((f) => isVoiceName(f.name)),
    docs: all.filter((f) => !isMediaName(f.name) && !isVoiceName(f.name)),
  };
}
